import React, { useState } from 'react';
import { AppHeader } from '../../../components/AppHeader/AppHeader';
import { useShellNav } from '../../../access/useShellNav';
import {
  Button,
  Icon,
  MenuLeaf,
  Modal,
  PageHeading,
  Pills,
  Section,
  Select,
  Sidebar,
  Submenu,
  Table,
  Tabs,
  Text,
} from 'barte-design-system';
import type { ColumnDefinition } from 'barte-design-system';
import {
  Flag,
  Lightning,
  Percent,
  Receipt,
  FileText,
  UsersThree,
} from '@phosphor-icons/react';
import { DrawerPendencias, PlanoPendencias } from '../Pendencias/DrawerPendencias';
import { SellerTagInput } from './SellerTagInput';
import './PlanoTaxas.css';
import './DetalhePlano.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PlanoTaxa {
  id: number;
  nome: string;
  totalVendedores: number | null;
}

interface Vendedor {
  id: number;
  nome: string;
  documento: string;
}

interface HistoricoAlteracao {
  id: number;
  responsavel: string;
  dataAlteracao: string;
  justificativa: string;
  pendencias: number;
}

// ─── Mock helpers ─────────────────────────────────────────────────────────────

const TEMPOS = ['D + 0', 'D + 1', 'D + 2', 'D + 30'];
const MARKUPS = ['0,00%', '0,20%', '0,40%', '0,60%', '0,80%', '1,00%'];
const TOTAIS = ['0,00%', '0,40%', '0,60%', '0,80%', '1,20%', '1,50%'];
const LIMITES = ['R$ 5.000,00', 'R$ 10.000,00', 'R$ 12.000,00', 'R$ 20.000,00', 'R$ 50.000,00'];
const TAXAS_BARTE = ['0,00%', '0,10%', '0,20%', '0,30%'];

const NOMES_SELLERS = [
  'FRD Serviços de Assessoria', 'Tech Store', 'FitLife Academia',
  'Ótica Visão Clara', 'Pet Shop Animal Love', 'Barbearia Corte Certo',
  'Lavanderia Express', 'Farmácia Saúde & Vida', 'Loja Central LTDA',
  'Mercado Bom Preço', 'Padaria Pão Quente', 'Restaurante Sabor Bom',
  'Supermercado Família', 'Auto Peças Brasil', 'Norte Distribuidora',
];

const PLANOS_OPTIONS = [
  { label: 'Plano seller 4821', value: '33' },
  { label: 'Plano Zero Template', value: '40' },
  { label: 'Plano - Allugator Serviços Digitais LTDA', value: '57' },
  { label: 'Plano - Razor Technologies do Brasil Ltda', value: '59' },
  { label: 'Plano Bronze Padrão', value: '70' },
];

function seededRandom(seed: number) {
  let s = seed;
  return () => {
    s = (s * 1664525 + 1013904223) & 0xffffffff;
    return Math.abs(s) / 0xffffffff;
  };
}

function getMockDados(id: number) {
  const rand = seededRandom(id * 31);
  const pick = <T,>(arr: T[]) => arr[Math.floor(rand() * arr.length)];
  return {
    pix:    { tempo: pick(TEMPOS), total: pick(TOTAIS), limite: pick(LIMITES) },
    boleto: { tempo: pick(TEMPOS), markup: pick(MARKUPS), total: pick(TOTAIS), limite: pick(LIMITES) },
    debito: { tempo: pick(TEMPOS), markup: pick(MARKUPS), taxaBarte: pick(TAXAS_BARTE), total: pick(TOTAIS) },
    credito:{ tempo: pick(TEMPOS), markup: pick(MARKUPS), taxaBarte: pick(TAXAS_BARTE), total: pick(TOTAIS) },
  };
}

function getMockVendedores(plano: PlanoTaxa): Vendedor[] {
  const count = plano.totalVendedores ?? 0;
  const rand = seededRandom(plano.id * 17);
  return Array.from({ length: count }, (_, i) => {
    const idx = Math.floor(rand() * NOMES_SELLERS.length);
    const doc = String(Math.floor(Math.abs(rand()) * 90000000000000 + 10000000000000));
    return { id: plano.id * 100 + i + 1, nome: NOMES_SELLERS[idx], documento: doc };
  });
}

const MOCK_HISTORICO: HistoricoAlteracao[] = [
  { id: 1, responsavel: 'Ana Paula Souza',     dataAlteracao: '09/04/2026 14:32', justificativa: 'Ajuste de markup solicitado pelo gestor de contas após revisão trimestral.', pendencias: 3 },
  { id: 2, responsavel: 'Carlos Henrique Lima', dataAlteracao: '01/04/2026 09:15', justificativa: 'Redução de taxa PIX conforme acordo comercial renovado.',                   pendencias: 0 },
  { id: 3, responsavel: 'Mariana Costa',        dataAlteracao: '22/03/2026 11:48', justificativa: 'Inclusão de limite para transações de débito.',                              pendencias: 1 },
  { id: 4, responsavel: 'Roberto Alves',        dataAlteracao: '10/03/2026 16:05', justificativa: 'Correção de taxa de boleto aplicada incorretamente na criação.',              pendencias: 2 },
  { id: 5, responsavel: 'Fernanda Rocha',       dataAlteracao: '28/02/2026 10:20', justificativa: 'Criação do plano.',                                                           pendencias: 0 },
];

const HISTORICO_COLUMNS: ColumnDefinition<HistoricoAlteracao>[] = [
  {
    key: 'responsavel',
    title: 'Responsável',
    dataIndex: 'responsavel',
    render: (v) => <Text variant="body-sm">{v as string}</Text>,
  },
  {
    key: 'dataAlteracao',
    title: 'Data da alteração',
    dataIndex: 'dataAlteracao',
    width: 180,
    render: (v) => <Text variant="body-sm">{v as string}</Text>,
  },
  {
    key: 'justificativa',
    title: 'Justificativa',
    dataIndex: 'justificativa',
    render: (v) => (
      <Text variant="body-sm">
        {(v as string).length > 100 ? (v as string).slice(0, 100) : (v as string)}
      </Text>
    ),
  },
  {
    key: 'pendencias',
    title: 'Pendências',
    dataIndex: 'pendencias',
    width: 160,
    render: (v) =>
      (v as number) === 0
        ? <Pills label="Sem pendências" variant="light" state="default" />
        : <Pills label={`${v as number} pendência${(v as number) > 1 ? 's' : ''}`} variant="light" state="accent" />,
  },
];

// ─── Columns ──────────────────────────────────────────────────────────────────

function buildColumns(onEditar: (v: Vendedor) => void): ColumnDefinition<Vendedor>[] {
  return [
    {
      key: 'id',
      title: 'ID',
      dataIndex: 'id',
      width: 80,
      render: (v) => <Text variant="label-sm">{v as number}</Text>,
    },
    {
      key: 'nome',
      title: 'Nome',
      dataIndex: 'nome',
      render: (v) => <Text variant="body-sm">{v as string}</Text>,
    },
    {
      key: 'documento',
      title: 'Documento',
      dataIndex: 'documento',
      width: 200,
      render: (v) => <Text variant="body-sm">{v as string}</Text>,
    },
    {
      key: 'actions',
      title: '',
      dataIndex: 'id',
      width: 140,
      render: (_v, record) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Button variant="ghost" size="sm" onClick={() => onEditar(record)}>
            Editar vínculo
          </Button>
        </div>
      ),
    },
  ];
}

// ─── Component ────────────────────────────────────────────────────────────────

interface DetalhePlanoProps {
  plano: PlanoTaxa;
  onBack: () => void;
}

export function DetalhePlano({ plano, onBack }: DetalhePlanoProps) {
  const { sidebarProps } = useShellNav();
  const [activeTab, setActiveTab] = useState<'detalhes' | 'vendedores' | 'historico'>('detalhes');
  const [modalVincular, setModalVincular] = useState(false);
  const [modalEditar, setModalEditar] = useState<Vendedor | null>(null);
  const [planoSelecionado, setPlanoSelecionado] = useState('');
  const [drawerPendencias, setDrawerPendencias] = useState<PlanoPendencias | null>(null);

  const dados = getMockDados(plano.id);
  const vendedores = getMockVendedores(plano);

  const COLUMNS = buildColumns((v) => {
    setPlanoSelecionado(String(plano.id));
    setModalEditar(v);
  });

  return (
    <div className="app-layout">
      <AppHeader variant="payfac" avatarProps={{ variant: 'icon' }} />

      <div className="app-body">
        <Sidebar {...sidebarProps}>
          <MenuLeaf label="Dashboard" icon={<Icon icon={Lightning} size={20} weight="regular" />} />
          <MenuLeaf label="Vendedores" icon={<Icon icon={UsersThree} size={20} weight="regular" />} />
          <MenuLeaf label="Transações" icon={<Icon icon={Receipt} size={20} weight="regular" />} />
          <MenuLeaf label="Contestações" icon={<Icon icon={Flag} size={20} weight="regular" />} />
          <MenuLeaf label="Taxas" icon={<Icon icon={Percent} size={20} weight="regular" />} isOpen>
            <Submenu label="Plano de Taxas" isActive />
            <Submenu label="Parceiros" />
            <Submenu label="Negociações" />
          </MenuLeaf>
          <MenuLeaf label="Relatórios" icon={<Icon icon={FileText} size={20} weight="regular" />} />
        </Sidebar>

        <main className="content">
          <div className="content-inner">
            <PageHeading title={plano.nome} onBack={onBack} />

            {/* ── Tabs + botão ───────────────────────────────────────── */}
            <div className="detalhe-tabs-row">
              <Tabs>
                <Tabs.Tab active={activeTab === 'detalhes'} onClick={() => setActiveTab('detalhes')}>
                  Detalhes do plano
                </Tabs.Tab>
                <Tabs.Tab active={activeTab === 'vendedores'} onClick={() => setActiveTab('vendedores')}>
                  Vendedores
                </Tabs.Tab>
                <Tabs.Tab active={activeTab === 'historico'} onClick={() => setActiveTab('historico')}>
                  Histórico de alterações
                </Tabs.Tab>
              </Tabs>
              {activeTab === 'vendedores' && (
                <Button size="sm" onClick={() => setModalVincular(true)}>
                  + Vincular seller
                </Button>
              )}
            </div>

            {/* ── Tab: Detalhes ─────────────────────────────────────── */}
            {activeTab === 'detalhes' && (
              <>
                <Section
                  title="Pix"
                  variant="two-columns"
                  headerActions={<Button variant="ghost" size="sm">Editar</Button>}
                  secondColumn={
                    <>
                      <Section.Row label="Tipo de taxa" content={<Text variant="label-sm">Porcentagem</Text>} />
                      <Section.Row label="Limite de taxas" content={<Text variant="label-sm">{dados.pix.limite}</Text>} />
                    </>
                  }
                >
                  <Section.Row label="Tempo de repasse" content={<Text variant="label-sm">{dados.pix.tempo}</Text>} />
                  <Section.Row label="Total Cobrado" content={<Text variant="label-sm">{dados.pix.total}</Text>} />
                </Section>

                <Section
                  title="Boleto"
                  variant="two-columns"
                  headerActions={<Button variant="ghost" size="sm">Editar</Button>}
                  secondColumn={
                    <>
                      <Section.Row label="Tipo de taxa" content={<Text variant="label-sm">Porcentagem</Text>} />
                      <Section.Row label="Total Cobrado" content={<Text variant="label-sm">{dados.boleto.total}</Text>} />
                    </>
                  }
                >
                  <Section.Row label="Tempo de repasse" content={<Text variant="label-sm">{dados.boleto.tempo}</Text>} />
                  <Section.Row label="Markup" content={<Text variant="label-sm">{dados.boleto.markup}</Text>} />
                  <Section.Row label="Limite de taxas" content={<Text variant="label-sm">{dados.boleto.limite}</Text>} />
                </Section>

                <Section
                  title="Cartão de Débito"
                  variant="two-columns"
                  headerActions={<Button variant="ghost" size="sm">Editar</Button>}
                  secondColumn={
                    <>
                      <Section.Row label="Tipo de taxa" content={<Text variant="label-sm">Porcentagem</Text>} />
                      <Section.Row label="Taxa Barte" content={<Text variant="label-sm">{dados.debito.taxaBarte}</Text>} />
                    </>
                  }
                >
                  <Section.Row label="Tempo de repasse" content={<Text variant="label-sm">{dados.debito.tempo}</Text>} />
                  <Section.Row label="Markup" content={<Text variant="label-sm">{dados.debito.markup}</Text>} />
                  <Section.Row label="Total Cobrado" content={<Text variant="label-sm">{dados.debito.total}</Text>} />
                </Section>

                <Section
                  title="Cartão de Crédito"
                  variant="two-columns"
                  headerActions={<Button variant="ghost" size="sm">Editar</Button>}
                  secondColumn={
                    <>
                      <Section.Row label="Tipo de taxa" content={<Text variant="label-sm">Porcentagem</Text>} />
                      <Section.Row label="Taxa Barte" content={<Text variant="label-sm">{dados.credito.taxaBarte}</Text>} />
                    </>
                  }
                >
                  <Section.Row label="Tempo de repasse" content={<Text variant="label-sm">{dados.credito.tempo}</Text>} />
                  <Section.Row label="Markup" content={<Text variant="label-sm">{dados.credito.markup}</Text>} />
                  <Section.Row label="Total Cobrado" content={<Text variant="label-sm">{dados.credito.total}</Text>} />
                </Section>
              </>
            )}

            {/* ── Tab: Vendedores ───────────────────────────────────── */}
            {activeTab === 'vendedores' && (
              <div className="content-table">
                <Table
                  data={vendedores}
                  columns={COLUMNS}
                  rowKey={(r) => r.id}
                  pagination={{
                    currentPage: 1,
                    totalPages: 1,
                    onPageChange: () => {},
                    pageSize: 50,
                    pageSizeOptions: [50],
                    onPageSizeChange: () => {},
                  }}
                />
              </div>
            )}

            {/* ── Tab: Histórico de alterações ──────────────────────── */}
            {activeTab === 'historico' && (
              <div className="content-table">
                <Table
                  data={MOCK_HISTORICO}
                  columns={HISTORICO_COLUMNS}
                  rowKey={(r) => r.id}
                  onRowClick={(record) =>
                    setDrawerPendencias({ nome: plano.nome, planoId: String(record.id) })
                  }
                  pagination={{
                    currentPage: 1,
                    totalPages: 1,
                    onPageChange: () => {},
                    pageSize: 50,
                    pageSizeOptions: [50],
                    onPageSizeChange: () => {},
                  }}
                />
              </div>
            )}
          </div>
        </main>
      </div>

      {/* ── Drawer: Pendências ────────────────────────────────────── */}
      <DrawerPendencias
        isOpen={!!drawerPendencias}
        onClose={() => setDrawerPendencias(null)}
        plano={drawerPendencias}
      />

      {/* ── Modal: Vincular seller ─────────────────────────────────── */}
      <Modal
        isOpen={modalVincular}
        title="Vincular seller"
        onClose={() => setModalVincular(false)}
        footer={{
          primaryButton: { children: 'Vincular', onClick: () => setModalVincular(false) },
          secondaryButton: { children: 'Cancelar', onClick: () => setModalVincular(false) },
        }}
      >
        <SellerTagInput label="Buscar seller" />
      </Modal>

      {/* ── Modal: Editar vínculo ──────────────────────────────────── */}
      <Modal
        isOpen={!!modalEditar}
        title="Editar vínculo"
        onClose={() => setModalEditar(null)}
        footer={{
          primaryButton: { children: 'Salvar', onClick: () => setModalEditar(null) },
          secondaryButton: { children: 'Cancelar', onClick: () => setModalEditar(null) },
        }}
      >
        {modalEditar && (
          <div className="detalhe-editar-modal">
            <div className="detalhe-editar-seller">
              <Text variant="label-md">Seller</Text>
              <Text variant="body-sm">{modalEditar.nome} - {modalEditar.id}</Text>
            </div>
            <Select
              label="Plano de taxas"
              options={PLANOS_OPTIONS}
              value={planoSelecionado}
              onChange={setPlanoSelecionado}
              searchable
            />
          </div>
        )}
      </Modal>
    </div>
  );
}
